<template>
  <!-- [sc-49299] Finestra di stampa: si sceglie cosa stampare e si vede subito
       il PDF. Il bottone consegna PDF e campi scelti a chi l'ha aperta (mixin
       stampaPreventivo): Nuovo e Modifica salvano solo a questo punto. -->
  <VueFinalModal
    :model-value="show"
    @update:model-value="!$event && chiudi()"
    :click-to-close="false"
    :esc-to-close="!salvando"
    classes="documents-modal"
    content-class="stampa-modal-content"
  >
    <template #default>
      <div class="stampa-header">
        <h3 class="modal__title">
          <template v-if="dati?.id">Stampa preventivo #{{ dati.id }}</template>
          <template v-else>Stampa nuovo preventivo</template>
        </h3>
        <button
          class="modal__close icons-button"
          type="button"
          title="Chiudi"
          :disabled="salvando"
          @click="chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="stampa-corpo">
        <aside class="stampa-campi">
          <p class="stampa-campi__aiuto">Togli quello che non vuoi stampare.</p>
          <fieldset v-for="g in gruppi" :key="g.titolo" class="stampa-gruppo">
            <legend>
              <label class="stampa-check stampa-check--gruppo">
                <input
                  type="checkbox"
                  :checked="gruppoTutto(g)"
                  :indeterminate.prop="gruppoInParte(g)"
                  @change="spuntaGruppo(g, $event.target.checked)"
                />
                {{ g.titolo }}
              </label>
            </legend>
            <label
              v-for="f in g.fissi || []"
              :key="f"
              class="stampa-check stampa-check--fisso"
              title="Si stampa sempre"
            >
              <input type="checkbox" checked disabled />
              {{ f }}
            </label>
            <label v-for="c in g.campi" :key="c.chiave" class="stampa-check">
              <input type="checkbox" v-model="campi[c.chiave]" />
              {{ c.label }}
            </label>
          </fieldset>
          <button
            type="button"
            class="stampa-tutto"
            :disabled="tuttoSpuntato"
            @click="campi = tuttiICampi()"
          >
            <i class="fa-solid fa-rotate-left"></i> Seleziona tutto
          </button>
        </aside>

        <section class="stampa-anteprima" aria-label="Anteprima del PDF">
          <iframe
            v-if="urlAnteprima"
            :src="urlAnteprima"
            title="Anteprima del preventivo"
          ></iframe>
          <p v-else class="stampa-anteprima__vuota">Preparo l'anteprima…</p>
        </section>
      </div>

      <div class="modal-actions stampa-azioni">
        <button
          type="button"
          class="secondary-btn"
          :disabled="salvando"
          @click="chiudi"
        >
          Annulla
        </button>
        <button
          type="button"
          class="primary-btn"
          :disabled="salvando || !dati"
          @click="scarica"
        >
          <i class="fa-solid fa-file-arrow-down"></i>
          {{ salvando ? "Salvo…" : etichettaScarica }}
        </button>
      </div>
    </template>
  </VueFinalModal>
</template>

<script>
import { VueFinalModal } from "vue-final-modal";
import logoPath from "@/assets/barcio-logo.png";
import { GRUPPI_STAMPA, tuttiICampi } from "@/utilities/campiStampa";
import { pdfPreventivo } from "@/utilities/pdfPreventivo";

export default {
  name: "StampaPreventivoModal",
  components: { VueFinalModal },
  props: {
    show: { type: Boolean, default: false },
    // dati del PDF (datiPdf), preparati dalla pagina che apre la finestra
    dati: { type: Object, default: null },
    // la pagina sta salvando il preventivo o caricando il PDF
    salvando: { type: Boolean, default: false },
    // Nuovo e Modifica salvano alla conferma: «Salva e scarica PDF»
    etichettaScarica: { type: String, default: "Scarica PDF" },
  },
  emits: ["update:show", "scarica", "chiudi"],
  data() {
    return {
      gruppi: GRUPPI_STAMPA,
      campi: tuttiICampi(),
      urlAnteprima: "",
      timer: null,
    };
  },
  computed: {
    tuttoSpuntato() {
      return Object.values(this.campi).every(Boolean);
    },
  },
  watch: {
    // ogni apertura riparte da tutto spuntato
    show: {
      handler(aperta) {
        if (aperta) {
          this.campi = tuttiICampi();
          this.aggiornaAnteprima(0);
        } else {
          this.liberaAnteprima();
        }
      },
      immediate: true,
    },
    dati() {
      if (this.show) this.aggiornaAnteprima(0);
    },
    campi: {
      deep: true,
      handler() {
        if (this.show) this.aggiornaAnteprima();
      },
    },
  },
  beforeUnmount() {
    this.liberaAnteprima();
  },
  methods: {
    tuttiICampi,
    gruppoTutto(g) {
      return g.campi.every((c) => this.campi[c.chiave]);
    },
    // a metà anche con tutte le caselle tolte, se resta un campo fisso
    gruppoInParte(g) {
      return (
        !this.gruppoTutto(g) &&
        (g.fissi?.length > 0 || g.campi.some((c) => this.campi[c.chiave]))
      );
    },
    spuntaGruppo(g, valore) {
      g.campi.forEach((c) => (this.campi[c.chiave] = valore));
    },
    documento() {
      return pdfPreventivo(this.dati, this.campi, { logo: logoPath });
    },
    // il PDF si rifà dopo una breve pausa: più clic di fila, un solo disegno
    aggiornaAnteprima(ritardo = 250) {
      clearTimeout(this.timer);
      if (!this.dati) return;
      this.timer = setTimeout(() => {
        try {
          const url = URL.createObjectURL(
            this.documento().output("blob", { filename: "anteprima.pdf" })
          );
          this.liberaAnteprima();
          this.urlAnteprima = `${url}#toolbar=0&navpanes=0&view=FitH`;
        } catch (err) {
          console.error("Anteprima non riuscita", err);
        }
      }, ritardo);
    },
    liberaAnteprima() {
      clearTimeout(this.timer);
      if (this.urlAnteprima) {
        URL.revokeObjectURL(this.urlAnteprima.split("#")[0]);
        this.urlAnteprima = "";
      }
    },
    scarica() {
      this.$emit("scarica", {
        doc: this.documento(),
        campi: { ...this.campi },
      });
    },
    chiudi() {
      if (this.salvando) return;
      this.$emit("update:show", false);
      this.$emit("chiudi");
    },
  },
};
</script>

<style>
.documents-modal .stampa-modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 1100px;
  max-width: calc(100vw - 32px);
  height: calc(100vh - 32px);
  box-sizing: border-box;
  background: var(--modal-bg);
  color: var(--modal-text);
  border: 1px solid var(--modal-border);
  border-radius: 12px;
  box-shadow: var(--modal-shadow);
  padding: 1.25rem;
}

.stampa-modal-content .stampa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stampa-modal-content .stampa-header .modal__title {
  margin: 0;
}

.stampa-modal-content .stampa-corpo {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 16px;
}

.stampa-modal-content .stampa-campi {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 4px;
}

.stampa-modal-content .stampa-campi__aiuto {
  margin: 0;
  font-size: 13px;
  color: var(--modal-muted);
}

.stampa-modal-content .stampa-gruppo {
  margin: 0;
  padding: 8px 10px;
  border: 1px solid var(--modal-border);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stampa-modal-content .stampa-gruppo legend {
  padding: 0 4px;
}

.stampa-modal-content .stampa-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
}

.stampa-modal-content .stampa-check--fisso {
  color: var(--modal-muted);
  cursor: default;
}

.stampa-modal-content .stampa-check--gruppo {
  font-weight: 600;
}

.stampa-modal-content .stampa-check input {
  width: auto;
  margin: 0;
  accent-color: var(--brand);
}

.stampa-modal-content .stampa-tutto {
  align-self: flex-start;
  padding: 5px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  cursor: pointer;
}

.stampa-modal-content .stampa-tutto:hover:not(:disabled) {
  background: #f4f4f5;
  border-color: var(--text);
}

.stampa-modal-content .stampa-tutto:disabled {
  opacity: 0.5;
  cursor: default;
}

.stampa-modal-content .stampa-anteprima {
  min-height: 0;
  border: 1px solid var(--modal-border);
  border-radius: 8px;
  overflow: hidden;
  background: #f4f4f5;
}

.stampa-modal-content .stampa-anteprima iframe {
  width: 100%;
  height: 100%;
  border: 0;
}

.stampa-modal-content .stampa-anteprima__vuota {
  margin: 2rem;
  color: var(--modal-muted);
  text-align: center;
}

.stampa-modal-content .stampa-azioni {
  justify-content: flex-end;
}

/* schermi stretti: le caselle sopra, l'anteprima sotto */
@media (max-width: 760px) {
  .stampa-modal-content .stampa-corpo {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(320px, 1fr);
  }

  .stampa-modal-content .stampa-campi {
    max-height: 40vh;
  }
}
</style>
